<template>
  <div class="event-component">
    <div class="more-info">
      <simple-dropdown :dropdown-arrow="true" class="more-info-dropdown"
                       :alignment="1"
                       :mouse-enter-to-show="true"
                       :mouse-enter-timeout="0"
                       :mouse-leave-timeout="0"
                       :click-dropdown-menu-to-hide="false"
      >
        <template v-slot:triggerButtonContent>
                    <span class="d-flex align-items-center">
                        <span class="mr-2"><span>More Info...</span></span>
                    </span>
        </template>
        <template v-slot:dropdownMenu>
          <div class="dropdown-content">
            <div class="event-description">
              <template v-if="eventNewlyCreated">
                <div class="description-text">
                  This is a new event you never submit before.
                </div>
              </template>
              <template v-else>
                <section class="properties">
                  <div class="property">
                    <span class="label">Event ID:</span> <span>{{ getEventId() }}</span>
                  </div>
                  <div class="property">
                    <span class="label">Modified:</span> <span>{{ describeUpdated }}</span>
                  </div>
                  <div class="property">
                    <span class="label">Created:</span> <span>{{ describeCreated }}</span>
                  </div>
                </section>
              </template>
            </div>
          </div>
        </template>
      </simple-dropdown>
    </div>
    <div class="document-issuer-display" v-if="documentIssuerItem">
      <span class="text-nowrap">Document Issuer —</span>
      <div class="entity-wrapper">
        <entity-item :item="documentIssuerItem"
                 :transform-suggestions-data="transformSuggestionsData"
                 :get-suggestion-item-h-t-m-l="getSuggestionItemHTML"
                 :removable="false"></entity-item>
      </div>
    </div>
    <div class="properties-form event">
      <div class="property-item more-dropdown-button-container" v-for="(field,index) in fields" :key="index"
           :data-field-id="field.field_id"
           :class="{'is-compulsory': field.is_compulsory, 'is-empty': isFieldEmpty(field),}">
        <div class="property-label" :class="{'has-more-dropdown-button': field.data_type==='entity' || 'multi_object'}"
             tabindex="-1">
          <span class="label">{{ toTitleCase(field['field_name']) }}</span>
          <span v-if="isFieldAlgorithmsPowered(field)" class="algorithms-powered-indicator"
                @mouseenter="onMouseEnterTooltipTarget"
                @mouseleave="onMouseLeaveTooltipTarget" data-tooltip-pos="up"
                data-tooltip="Algorithms Powered">
                        <i class="gf gf-bulb"></i>
                    </span>
          <create-entity-dropdown class="more-dropdown-button"
                                  @copyEntities="copyEntitiesFrom(field.field_id)"
                                  @pasteEntities="pasteEntitiesTo(field.field_id)"
                                  v-if="inEditWorkMode && field.data_type==='entity'"></create-entity-dropdown>
          <create-multi-box-drop-down class="more-dropdown-button"
                                  @copyFormValue="copyMultiFormValueFrom(field.field_id)"
                                  @pasteFormValue="pasteMultiFormValueTo(field.field_id)"
                                  v-if="inEditWorkMode && field.data_type==='multi_object'"></create-multi-box-drop-down>
        </div>
        <div class="property-control" v-if="inEditWorkMode">
          <component :is="getInputControlForField(field).component"
                     :initial-value="getInitialValueForFieldId(field['field_id'])"
                     :parentValues="fieldIdToValueMaps"
                     v-bind="getInputControlForField(field).props"
                     :property-in-form="field['field_id']"
                     :locate-button="true"
                     @valueChanged="valueChanged"
                     @highlightTextInViewerForItem="highlightTextInViewerForItem"
                     ref="inputControls"
          ></component>
        </div>
        <div class="property-value-displayer" v-if="inReadWorkMode">
          <component :is="getInputValueDisplayerForField(field).component"
                     :value="getConfirmedValueForFieldId(field['field_id'])"
                     v-bind="getInputValueDisplayerForField(field).props"
                     :field="field"
                     :candidates="getCandidatesForFieldId(field['field_id'])"
                     @highlightTextInViewerForItem="highlightTextInViewerForItem"
          >
          </component>
        </div>
        <div class="shortcuts" v-if="inEditWorkMode">
          <button
              v-if="field.data_type === 'text' && (field.is_note_field || field.is_compulsory) && fieldIdsEmptyMaps[field.field_id]"
              @click="setInputValue(field.field_id, 'Not Mentioned')">Not Mentioned
          </button>
          <template v-if="Array.isArray(field.math_formula)">
            <template v-for="(formula, index) in field.math_formula">
              <button :key="index" class="mt-1" v-if="canLoadFormulaToField(field, index)" @click="updateFieldByFormula(field, index)">
                Load Formula <span>{{ index+1 }}</span>
              </button>
            </template>
          </template>
          <template v-else>
            <button class="mt-1" v-if="canLoadFormulaToField(field)" @click="updateFieldByFormula(field)">
              Load Formula
            </button>
          </template>

        </div>
        <div class="value-candidates" v-if="inEditWorkMode && getCandidatesForFieldId(field['field_id'])">
          <div class="header" @click="toggleFieldCandidates(field.field_id)" tabindex="-1">
                        <span>Candidates <span
                            class="count">({{ getCandidatesForFieldId(field['field_id']).length }})</span>:</span>
            <button class="candidates-toggle" tabindex="-1">
                            <span v-if="fieldIdsCandidatesHiddenMaps[field.field_id]"><i
                                class="gf gf-angle-down"></i></span>
              <span v-else><i class="gf gf-angle-up"></i></span>
            </button>
          </div>
          <ul class="list" v-if="!fieldIdsCandidatesHiddenMaps[field.field_id]">
            <li v-for="(item,index) in getCandidatesForFieldId(field['field_id'])" :key="index"
                :class="getCandidateItemClazz(item, field['field_id'])">
              <div class="main-button" @click="onCandidateItemClicked(item, field)">
                <i class="before"></i><span :title="getCandidateItemLabel(item, field)"
                                            class="label-text">{{
                  getCandidateItemLabel(item, field)
                }}</span>
              </div>
              <i class="after" @click="onCandidateItemContextButtonClicked(item)"></i>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  deepCompare,
  describeUnixTs,
  describeUsername,
  evaluateFieldValueFilled,
  evaluateValueFilled,
  renderValueInCandidateItem,
  toTitleCase,
  userSettingsManager,
  workModeEdit,
  workModeRead,
} from "@/utils";
import SimpleDropdown from "@/components/SimpleDropdown";
import PDBasic from "@/components/PropertyDisplayers/PDBasic";
import PDEntityBox from "@/components/PropertyDisplayers/PDEntityBox";
import PDMultiObj from "@/components/PropertyDisplayers/PDMultiObj";
import {Promise} from 'es6-promise-polyfill';
import {getInputControlForField} from "@/InputControls/InputControlTools";
import CreateEntityDropdown from "@/components/CreateEntityDropdown";
import CreateMultiBoxDropDown from "@/components/CreateMultiBoxDropDown";
import FormulaControlBase from "@/InputControls/FormulaControlBase";
import EntitiesCopyBase from "@/InputControls/EntitiesCopyBase";
import MultiObjectCopyBase from "@/InputControls/MultiObjectCopyBase";
import EntityItem from "@/components/EntityItem";
import {itemValueProperty} from "@/config";
import {transformSuggestionsData, getSuggestionItemHTML} from '@/InputControls/AutocompleteBox'

export default {
  name: "DTEvent",
  components: {EntityItem, CreateEntityDropdown, CreateMultiBoxDropDown, SimpleDropdown},
  props: {
    eventObject: Object,
    fieldsTemplate: Object,
    preprocessingResults: Object,
    projectId: String,
    documentObject: Object
  },
  data() {
    return {
      initialized: false,
      fieldIdsEmptyMaps: {},
      fieldIdToValueMaps: {},
      fieldIdsCandidatesHiddenMaps: {},
      initializedPromise: null,
      initializedResolvers: [],
    }
  },
  mounted() {
    this.initializedPromise = new Promise((resolve) => {
      this.initializedResolvers.push(resolve);
    });
    this.$nextTick(() => {
      this.initializeControls();
    })
  },
  computed: {
    fields() {
      return this.fieldsTemplate && this.fieldsTemplate['fields'] || [];
    },
    fieldIdToSchema() {
      const fieldIdToSchema = {};
      this.fields.forEach(item => {
        fieldIdToSchema[item.field_id] = item;
      });
      return fieldIdToSchema;
    },
    eventNewlyCreated() {
      return this.getEventId()[0] === ':';
    },
    describeCreated() {
      const eventObject = this.eventObject;
      let createTime = eventObject['create_time'];
      let createdBy = eventObject['created_by'];

      if (createTime) {
        return `${describeUnixTs(createTime)} by ${describeUsername(createdBy, 'Unknown')}`;
      } else {
        return 'Unknown';
      }
    },
    describeUpdated() {
      const eventObject = this.eventObject;
      let lastUpdate = eventObject['last_update'];
      let lastUpdateAdmin = eventObject['last_update_admin'];

      if (lastUpdate) {
        return `${describeUnixTs(lastUpdate)} by ${describeUsername(lastUpdateAdmin, 'Unknown')}`;
      } else {
        return 'Never';
      }
    },
    workMode() {
      return userSettingsManager.getWorkMode();
    },
    inEditWorkMode() {
      return this.workMode === workModeEdit;
    },
    inReadWorkMode() {
      return this.workMode === workModeRead;
    },
    documentIssuerItem() {
      const {custom_data: {gekko_id_company}} = this.documentObject;
      return gekko_id_company ? {[itemValueProperty]: String(gekko_id_company)} : null;
    }
  },
  watch: {
    workMode(newVal) {
      if (newVal === workModeEdit) {
        this.$nextTick(() => {
          this.initializeControls();
        })
      }
    }
  },
  methods: {
    isFieldAlgorithmsPowered(field) {
      return field.algo_powered;
    },
    isFieldEmpty(field) {
      return this.fieldIdsEmptyMaps && this.fieldIdsEmptyMaps[field.field_id]
    },
    initializeControls() {
      if (this.initialized) {
        return;
      }

      if (this.fields.length === 0) {
        this.initialized = true;
        this.initializedResolvers.forEach(r => {
          r();
        });
        return;
      }

      if (!this.$refs.inputControls) {
        return;
      }

      let map = {};
      let map2 = {};
      let isEmpty;
      let field, comp, value;
      this.$refs.inputControls.forEach(control => {
        value = control.getValue();
        field = this.getFieldTemplateByFieldId(control.propertyInForm);
        isEmpty = !evaluateFieldValueFilled(value, field);
        if (isEmpty) {
          if (typeof field.note !== "undefined" && field.note !== null) {
            if ((comp = this.getInputControlComponentByFieldId(field.note))) {
              isEmpty = !evaluateValueFilled(comp.getValue());
            }
          }
        }
        map[control.propertyInForm] = isEmpty;
        map2[control.propertyInForm] = value;
      });
      this.fieldIdsEmptyMaps = map;
      this.fieldIdToValueMaps = map2;

      this.initialized = true;
      this.initializedResolvers.forEach(r => {
        r();
      });
    },
    toTitleCase: toTitleCase,
    toggleFieldCandidates(fieldId) {
      this.$set(this.fieldIdsCandidatesHiddenMaps, fieldId, !this.fieldIdsCandidatesHiddenMaps[fieldId]);
    },
    getInputControlComponentByFieldId(fieldId) {
      return this.$refs.inputControls && this.$refs.inputControls.filter(item => (item.propertyInForm === fieldId))[0] || null
    },
    getFieldTemplateByFieldId(fieldId) {
      return this.fieldIdToSchema[fieldId];
    },
    getNoteForFieldTemplate(noteFieldId) {
      return this.fields.filter(item => (item.note === noteFieldId))[0];
    },
    resetForm(formData) {
      this.$refs.inputControls && this.$refs.inputControls.forEach(vm => {
        vm.setInputValue(formData[vm.propertyInForm]);
      });
    },
    valueChanged(newVal, oldVal, fieldId) {
      if (!this.initialized) {
        return;
      }

      this.fieldIdToValueMaps[fieldId] = newVal;
      this.updateFieldIdsEmptyMapsForField(fieldId);

      let field = this.getNoteForFieldTemplate(fieldId);

      if (typeof field !== "undefined") {
        this.updateFieldIdsEmptyMapsForField(field.field_id);
      }

      this.$emit('valueChanged');
    },
    updateFieldIdsEmptyMapsForField(fieldId) {
      let control = this.getInputControlComponentByFieldId(fieldId);
      let field = this.getFieldTemplateByFieldId(fieldId);
      let isEmpty = !evaluateFieldValueFilled(control.getValue(), field);
      let comp;

      if (isEmpty) {
        if (typeof field.note !== "undefined" && field.note !== null) {
          comp = this.getInputControlComponentByFieldId(field.note);
          if (!comp) {
            // eslint-disable-next-line no-console
            console.error('the note field is null:', field, field.note)
          }
          isEmpty = !evaluateValueFilled(comp.getValue());
        }
      }

      this.fieldIdsEmptyMaps[fieldId] = isEmpty;
    },
    getEventId() {
      return this.eventObject['event_id'];
    },
    getSerialId() {
      return this.eventObject['serial_id'];
    },
    getFormValues() {
      if (!this.initialized) {
        throw new Error('Not fully initialized!')
      }
      const values = {};
      this.$refs.inputControls && this.$refs.inputControls.forEach(vm => {
        values[vm.propertyInForm] = vm.getValue();
      });
      return JSON.parse(JSON.stringify(values));
    },
    getCandidatesForFieldId(fieldId) {
      const pr = this.preprocessingResults;
      return pr && pr[fieldId] && pr[fieldId]['candidates'] || null;
    },
    getAutoFilledValueForFieldId(fieldId) {
      const pr = this.preprocessingResults;
      return pr && pr[fieldId] && pr[fieldId]['auto_filled'];
    },
    getConfirmedValueForFieldId(fieldId) {
      const eventObject = this.eventObject;
      return eventObject && eventObject.values && eventObject.values[fieldId];
    },
    getInitialValueForFieldId(fieldId) {
      let v = this.getConfirmedValueForFieldId(fieldId);
      if (typeof v === 'undefined') {
        v = this.getAutoFilledValueForFieldId(fieldId);
      }
      if (typeof v === 'undefined' && this.eventNewlyCreated) {
        const fieldTemplate = this.getFieldTemplateByFieldId(fieldId);
        const {default_value, prefill} = fieldTemplate;
        if (typeof default_value !== 'undefined') {
          v = default_value;
        }
        if (prefill) {
          let gekko_id_company;
          switch (prefill) {
            case 'document_issuer':
              ({custom_data: {gekko_id_company}} = this.documentObject);
              if (typeof gekko_id_company !== 'undefined' && gekko_id_company !== null) {
                v = [String(gekko_id_company)];
              }
              break;
            default:
              // eslint-disable-next-line no-console
              console.error('prefill not recognized:', prefill);
          }
        }
      }
      return v;
    },
    getInputControlForField: getInputControlForField,
    getInputValueDisplayerForField(field) {
      let component;
      let props = {};

      if (field.data_type === 'entity') {
        component = PDEntityBox;
      } else if (field.data_type === 'multi_object') {
        component = PDMultiObj;
        props = {
          template: field['object_items'],
        }
      } else {
        component = PDBasic;
      }

      return {
        component,
        props,
      }
    },
    getCandidateItemLabel(item, field) {
      if (evaluateFieldValueFilled(item.value, field)) {
        return this.renderValueInCandidateItem(item.value, field);
      }
      return item.context || '';
    },
    renderValueInCandidateItem: renderValueInCandidateItem,
    getCandidateItemClazz(item, fieldId) {
      const field = this.getFieldTemplateByFieldId(fieldId);
      const clazz = [];
      if (evaluateFieldValueFilled(item.value, field)) {
        clazz.push('has-value');

        if (deepCompare(item.value, this.fieldIdToValueMaps[fieldId])) {
          clazz.push('value-selected');
        }
      }
      if (evaluateValueFilled(item.context)) {
        clazz.push('has-context');
      }
      return clazz;
    },
    setInputValue(fieldId, value, focus) {
      if (typeof focus === 'undefined') {
        focus = true;
      }
      const controls = this.$refs.inputControls;
      for (let i = 0; i < controls.length; i++) {
        if (controls[i].propertyInForm === fieldId) {
          focus && controls[i].focus();
          controls[i].setInputValue(value);
          break
        }
      }
    },
    onCandidateItemClicked(item, field) {
      if (evaluateFieldValueFilled(item.value, field)) {
        this.setInputValue(field['field_id'], item.value);
        return;
      }
      if (evaluateValueFilled(item.context)) {
        this.highlightTextInViewerForItem(item);
      }
    },
    onCandidateItemContextButtonClicked(item) {
      this.highlightTextInViewerForItem(item);
    },
    highlightTextInViewerForItem(item) {
      let text = item.context.trim();
      if (evaluateValueFilled(item.seed)) {
        text += ':::' + item.seed;
      }
      this.$emit('highlightTextInViewer', text);
    },
    transformSuggestionsData: transformSuggestionsData,
    getSuggestionItemHTML: getSuggestionItemHTML,
  },
  mixins: [FormulaControlBase, EntitiesCopyBase, MultiObjectCopyBase]
}
</script>

<style scoped lang="scss">
@import "../assets/variables";

.description {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.86em;
  margin-bottom: 0.5rem;
  margin-top: -0.5rem;

  .description-content {
    background-color: white;
    border-radius: 6px;
    padding: 2px 0.5rem;
  }
}

.more-info {
  margin: -0.75rem 0 0.5rem;
  display: flex;
  justify-content: center;
}

.document-issuer-display {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 0.8em;
  justify-content: center;

  .entity-wrapper {
    margin-left: 0.25rem;
    overflow: hidden;
  }
}
</style>
